
import { Component, Prop, Vue, Watch } from "vue-property-decorator"
import { CaseColumn } from "@/types/caseColumns"

import vSelect from "vue-select"
import { DataType, InsightCalculation, InsightCalculationTotal } from "@/types/insights"
import { cloneDeep } from "lodash"

import ListSelect from "@/components/Controls/ListSelect.vue"
import CalculationExpression from "./CalculationExpression.vue"
import Guid from "@/scripts/guid"

enum CollationMode {
	All,
	Include,
	Exclude
}

@Component({
	components: {
		"v-select": vSelect,
		"list-select": ListSelect,
		"calculation-expression": CalculationExpression
	}
})
export default class CalculationTotals extends Vue {
	@Prop({ default: () => [] })
	value!: InsightCalculationTotal[]

	@Prop({ default: () => [] })
	fields!: CaseColumn[]

	@Prop({ default: () => [] })
	insightCalculations!: InsightCalculation[]

	controlId: string = ""
	collationMode = CollationMode

	calculationTotalsColumns: {key: string, label: string}[] = [
		{
			key: "title",
			label: "Title"
		},
		{
			key: "groupBy",
			label: "Group By"
		},
		{
			key: "calculationList",
			label: "Calculations"
		},
		{
			key: "subtractCalculationList",
			label: "Subtract Calculations"
		},
		{
			key: "expression",
			label: "Expression"
		},
		{
			key: "buttons",
			label: ""
		}
	]

	get groupByCalculationFields (): any[] {
		if (!this.fields) {
			return []
		}

		const groupableDataTypes = [DataType.boolean, DataType.number, DataType.dateTime, DataType.array, DataType.arrayMultiple, DataType.userRole]
		return this.fields.filter(f => groupableDataTypes.includes(f.fieldDataType!))
	}

	private mounted () {
		this.controlId = Guid.newGuid()
	}

	private insightsCalculationTotalList (total: InsightCalculationTotal, groupBy: string): InsightCalculation[] {
		if (!total || !total.calculationList || total.calculationList.length === 0) {
			return this.insightCalculations
		}

		return groupBy
			? this.insightCalculations.filter(c => c.groupBy === groupBy)
			: this.insightCalculations.filter(c => !c.groupBy)
	}

	showCalculationFormattingRules (calculation: InsightCalculation | null) {
		this.$emit("showCalculationFormattingRules", calculation)
	}

	/**
	 * Adds a new empty calculation total to the insight if there are no existing totals or all existing totals are valid
	 * @private
	 */
	private addCalculationTotal (): void {
		const newValue = this.value ? cloneDeep(this.value) : []
		newValue.push({
			key: Guid.newGuid(),
			title: "",
			calculationList: [],
			subtractCalculationList: [],
			groupBy: "",
			expression: "",
			collate: false
		})

		this.$emit("input", newValue)
	}

	/**
	 * Removes a specified calculation total from the insight.
	 * @param calculationTotal The calculationTotal to remove from the insight.
	 * @private
	 */
	private removeCalculationTotal (calculationTotal: InsightCalculationTotal): void {
		if (this.value) {
			this.$emit("input", this.value.filter(t => t !== calculationTotal))
		}
	}

	private allowCollation (groupBy: string | null) {
		if (!groupBy) return false

		const field = this.fields.find(f => f.key === groupBy)
		if (!field) return false

		return field.dataType === "list" || field.dataType === "list-multiple" || field.dataType === "list-data"
	}

	private setCollationMode (calculationTotal: InsightCalculationTotal, collateMode: CollationMode) {
		switch (collateMode) {
			case CollationMode.All:
				Vue.set(calculationTotal, "collateInclude", null)
				Vue.set(calculationTotal, "collateExclude", null)
				break
			case CollationMode.Include:
				Vue.set(calculationTotal, "collateInclude", [])
				Vue.set(calculationTotal, "collateExclude", null)
				break
			case CollationMode.Exclude:
				Vue.set(calculationTotal, "collateInclude", null)
				Vue.set(calculationTotal, "collateExclude", [])
				break
		}
	}

	private getCollationMode (calculationTotal: InsightCalculationTotal): CollationMode {
		return calculationTotal.collateInclude
			? CollationMode.Include
			: calculationTotal.collateExclude
				? CollationMode.Exclude
				: CollationMode.All
	}

	private groupByField (groupBy: string) {
		return this.fields.find(f => f.key === groupBy)
	}

	private calculationAdded (calculationTotal: InsightCalculationTotal, calculations: string[]) {
		if (calculations.length > 0) {
			const groupBy = calculations
				.map(c => this.insightCalculations.findAndGet(calculation => calculation.key === c, c => c.groupBy, null))
				.distinct()
			calculationTotal.groupBy = groupBy.length > 0 ? groupBy[0] : null
		}
	}
}
